import type * as GeoJSON from "geojson";
import { simWeatherFromRaw, SimWeatherData } from "./storyWeather";

import { detectIgnitionGrid } from "../../utils/ignitionGrid";
import type { UnitSystem } from "../../utils/units";
import {
  RISK_FROM_CELL_ALPHA,
  RISK_FROM_POINT_RADIUS,
  RISK_FROM_RAMP,
  RISK_FROM_SHAPE_ALPHA,
  hexToRgba,
  riskFromClass,
  riskFromMetric,
} from "../layer_components/riskFromStyles";
import {
  FrozenSimLayer,
  FrozenSimLayerIgnition,
  RiskFromMetric,
} from "../../types/storyMap";

/**
 * Story-side derivations of the project page's simulation layers (Risk from /
 * Risk to / Inspection), as pure functions over one normalised data shape.
 *
 * The builder/preview feed it the live fireSimulationResult response; the
 * public viewer feeds it the frozen `mapData.simLayers` slice — so what a
 * reader sees is exactly what the author saw, minus interactivity.
 */

/** One ignition; `finalFrame` is only present on live (authed) data. */
export interface SimLayerIgnition extends FrozenSimLayerIgnition {
  finalFrame?: GeoJSON.FeatureCollection | null;
}

export interface SimLayerData {
  /** Weather inputs (uniform series + optional lattice), for the weather layer. */
  weather?: SimWeatherData | null;
  latStep?: number | null;
  lngStep?: number | null;
  totalBurntAreaImg?: string | null;
  isEnsemble?: boolean;
  members?: number | null;
  pointsPerMember?: number | null;
  ignitions: SimLayerIgnition[];
}

/** Global partition index off an ignition's partition string, or null. */
export function ignitionPartitionIndex(
  ignition: SimLayerIgnition
): number | null {
  const idx = parseInt(String(ignition.partition ?? "").split("_")[0], 10);
  return Number.isFinite(idx) ? idx : null;
}

/** Relevant slice of one wire ignition in the fireSimulationResult response. */
interface RawResultIgnition {
  start_point_lat: number;
  start_point_lng: number;
  start_geometry?: GeoJSON.Geometry | null;
  geometry?: GeoJSON.Geometry | null;
  ha_burnt?: number | null;
  asset_impacts?: number | null;
  partition?: string | null;
  final_animation_frame?: GeoJSON.FeatureCollection | null;
}

/** Relevant slice of one wire result in the fireSimulationResult response. */
interface RawResult {
  lat_step?: number | null;
  lng_step?: number | null;
  total_burnt_area_img?: string | null;
  ignitions?: RawResultIgnition[];
  start_date_time?: string | null;
  weather?: unknown;
  weather_grid?: unknown;
}

/**
 * Normalise the authed fireSimulationResult response (results[0] — saved sims
 * collate to a single entry) into SimLayerData. Frozen mapData.simLayers
 * entries are already this shape.
 */
export function simLayerFromResults(
  results: unknown[] | null | undefined
): SimLayerData | null {
  const first = Array.isArray(results) ? (results[0] as RawResult) : null;
  if (!first || !Array.isArray(first.ignitions) || first.ignitions.length === 0) {
    return null;
  }
  return {
    weather: simWeatherFromRaw(first),
    latStep: first.lat_step ?? null,
    lngStep: first.lng_step ?? null,
    totalBurntAreaImg: first.total_burnt_area_img ?? null,
    ignitions: first.ignitions
      .filter((ign) => ign && typeof ign === "object")
      .map((ign) => ({
        lat: ign.start_point_lat,
        lng: ign.start_point_lng,
        geometry: ign.start_geometry ?? ign.geometry ?? null,
        haBurnt: ign.ha_burnt ?? null,
        assetImpacts: ign.asset_impacts ?? null,
        partition: ign.partition ?? null,
        finalFrame: ign.final_animation_frame ?? null,
      })),
  };
}

function squarePolygon(
  lat: number,
  lng: number,
  latHalf: number,
  lngHalf: number
): GeoJSON.Polygon {
  return {
    type: "Polygon",
    coordinates: [
      [
        [lng - lngHalf, lat - latHalf],
        [lng + lngHalf, lat - latHalf],
        [lng + lngHalf, lat + latHalf],
        [lng - lngHalf, lat + latHalf],
        [lng - lngHalf, lat - latHalf],
      ],
    ],
  };
}

/**
 * Detect a regular ignition grid from point spacing, at the detected spacing
 * rather than the simulation's raster step. Shared with the project page's
 * Risk From layer (utils/ignitionGrid) so every mirror agrees on what is a
 * grid — in particular that ensemble replicas of a couple of point ignitions
 * are not one.
 */
export function detectGridSteps(data: SimLayerData): {
  latStep: number;
  lngStep: number;
  isGridded: boolean;
} {
  return detectIgnitionGrid({
    latStep: data.latStep,
    lngStep: data.lngStep,
    ignitions: data.ignitions.map((i) => ({ lat: i.lat, lng: i.lng })),
  });
}

function displayGeometry(
  ignition: SimLayerIgnition,
  latStep: number,
  lngStep: number,
  isGridded: boolean
): GeoJSON.Geometry {
  const geom = ignition.geometry as GeoJSON.Geometry | null | undefined;
  const geomType = geom?.type;
  if (
    geomType === "LineString" ||
    geomType === "MultiLineString" ||
    geomType === "Polygon" ||
    geomType === "MultiPolygon"
  ) {
    return geom as GeoJSON.Geometry;
  }
  if (geomType === "Point") {
    const [lng, lat] = (geom as GeoJSON.Point).coordinates as [number, number];
    if (!isGridded) return { type: "Point", coordinates: [lng, lat] };
    return squarePolygon(lat, lng, latStep / 2, lngStep / 2);
  }
  if (!isGridded) {
    return { type: "Point", coordinates: [ignition.lng, ignition.lat] };
  }
  return squarePolygon(ignition.lat, ignition.lng, latStep / 2, lngStep / 2);
}

/**
 * Risk-from features: one per ignition location (ensemble members replicating
 * a location collapse to its mean), coloured blue→red across the metric range.
 */
export function buildRiskFromGeojson(
  data: SimLayerData | null | undefined,
  metric: RiskFromMetric = "fire_size",
  unitSystem: UnitSystem = "metric"
): GeoJSON.FeatureCollection | null {
  if (!data || data.ignitions.length === 0) return null;
  // Same fixed class ramp as the project page's Risk From layer, so a story
  // scene reads on the scale the author saw (and fire-size classes follow the
  // unit system the story was published in).
  const def = riskFromMetric(metric, unitSystem);

  const byLocation = new Map<
    string,
    { ignition: SimLayerIgnition; ha: number; assets: number; count: number }
  >();
  data.ignitions.forEach((ign) => {
    const key = `${ign.lat},${ign.lng}`;
    const entry = byLocation.get(key);
    if (entry) {
      entry.ha += ign.haBurnt ?? 0;
      entry.assets += ign.assetImpacts ?? 0;
      entry.count += 1;
    } else {
      byLocation.set(key, {
        ignition: ign,
        ha: ign.haBurnt ?? 0,
        assets: ign.assetImpacts ?? 0,
        count: 1,
      });
    }
  });
  const collapsed = Array.from(byLocation.values()).map((e) => ({
    ignition: e.ignition,
    value:
      metric === "impacted_assets" ? e.assets / e.count : e.ha / e.count,
  }));

  const { latStep, lngStep, isGridded } = detectGridSteps(data);

  return {
    type: "FeatureCollection",
    features: collapsed.map((c, index) => {
      const klass = riskFromClass(c.value, def);
      const color = RISK_FROM_RAMP[klass];
      const geometry = displayGeometry(c.ignition, latStep, lngStep, isGridded);
      // Grid cells carry a per-class alpha (low classes stay a light veil);
      // polygon ignitions use the flat shape alpha.
      const alpha = isGridded && geometry.type === "Polygon" ? RISK_FROM_CELL_ALPHA[klass] : RISK_FROM_SHAPE_ALPHA;
      return {
        type: "Feature",
        geometry,
        properties: {
          id: `risk-from-${index}`,
          color,
          fill: hexToRgba(color, alpha),
          radius: RISK_FROM_POINT_RADIUS[klass],
        },
      };
    }),
  };
}

/** Bounding box of any GeoJSON feature/geometry (walks all coordinates). */
function bboxOf(
  feature: GeoJSON.Feature | null | undefined
): { left: number; right: number; top: number; bottom: number } | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const walk = (coords: unknown) => {
    if (!Array.isArray(coords)) return;
    if (
      coords.length >= 2 &&
      typeof coords[0] === "number" &&
      typeof coords[1] === "number"
    ) {
      west = Math.min(west, coords[0]);
      east = Math.max(east, coords[0]);
      south = Math.min(south, coords[1]);
      north = Math.max(north, coords[1]);
      return;
    }
    coords.forEach(walk);
  };
  walk((feature?.geometry as { coordinates?: unknown } | null)?.coordinates);
  if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
  return { left: west, right: east, top: north, bottom: south };
}

/**
 * Risk-to raster overlay: the simulation's total-burnt-area image (PNG data
 * URI) draped over the project boundary bbox — mirrors RiskToLayer.
 */
export function buildRiskToImage(
  data: SimLayerData | null | undefined,
  boundaryFeature: GeoJSON.Feature | null | undefined
): {
  url: string;
  coordinates: [
    [number, number],
    [number, number],
    [number, number],
    [number, number]
  ];
} | null {
  if (!data?.totalBurntAreaImg) return null;
  const bbox = bboxOf(boundaryFeature);
  if (!bbox) return null;
  const { top, left, bottom, right } = bbox;
  return {
    url: data.totalBurntAreaImg,
    coordinates: [
      [left, top],
      [right, top],
      [right, bottom],
      [left, bottom],
    ],
  };
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function footprintContains(
  frame: GeoJSON.FeatureCollection | null | undefined,
  point: [number, number]
): boolean {
  const [lng, lat] = point;
  for (const feature of frame?.features ?? []) {
    const geometry = feature?.geometry as GeoJSON.Geometry | null;
    if (geometry?.type === "Polygon") {
      if (pointInRing(lng, lat, geometry.coordinates[0] as number[][])) {
        return true;
      }
    } else if (geometry?.type === "MultiPolygon") {
      for (const poly of geometry.coordinates) {
        if (pointInRing(lng, lat, poly[0] as number[][])) return true;
      }
    }
  }
  return false;
}

/**
 * Inspection features: a cell at each ignition origin, yellow when that
 * ignition's final burn footprint contains `point` (or when no point is set),
 * transparent otherwise — mirrors InspectionLayer.
 *
 * Live data hit-tests `finalFrame` client-side; the public viewer passes the
 * per-scene `frozenVisibility` array resolved by the publish freeze instead.
 * Ignitions with neither (ensembles strip footprints) are skipped.
 */
export function buildInspectionGeojson(
  data: SimLayerData | null | undefined,
  point: [number, number] | null | undefined,
  frozenVisibility?: (boolean | null)[] | null
): GeoJSON.FeatureCollection | null {
  if (!data || data.ignitions.length === 0) return null;
  // Cell size from the detected ignition-grid spacing (falling back to the
  // raster step) so gridded regimes render as contiguous, joined-up cells.
  const { latStep, lngStep } = detectGridSteps(data);
  const latHalf = latStep / 2;
  const lngHalf = lngStep / 2;

  const features: GeoJSON.Feature[] = [];
  // Ensembles replicate every base location once per member; emit one cell
  // per location (the first non-null instance) so translucent squares don't
  // stack into an opaque block.
  const seenLocations = new Set<string>();
  data.ignitions.forEach((ignition, index) => {
    let visible: boolean | null;
    if (frozenVisibility) {
      visible = frozenVisibility[index] ?? null;
    } else if (!ignition.finalFrame) {
      visible = null;
    } else {
      visible = point ? footprintContains(ignition.finalFrame, point) : true;
    }
    if (visible === null) return;
    const locationKey = `${ignition.lat},${ignition.lng}`;
    if (seenLocations.has(locationKey)) return;
    seenLocations.add(locationKey);
    features.push({
      type: "Feature",
      geometry: squarePolygon(ignition.lat, ignition.lng, latHalf, lngHalf),
      properties: {
        id: `inspection-${index}`,
        color: visible ? "#ffff00" : "transparent",
      },
    });
  });
  if (features.length === 0) return null;
  return { type: "FeatureCollection", features };
}

/**
 * Per-ignition inspection visibility for an ENSEMBLE, synthesised client-side
 * from prefetched per-base-point footprints — the same array
 * shape the publish freeze writes, so buildInspectionGeojson consumes both.
 *
 * `memberIdx` scopes the answer to one member (its other instances become
 * null and are skipped); null answers everything (confidence). A null
 * `impactedByPoint` means no point is selected — every relevant origin shows.
 */
export function ensembleInspectionVisibility(
  data: SimLayerData,
  pointsPerMember: number,
  memberIdx: number | null,
  impactedByPoint: Map<number, boolean | null> | null
): (boolean | null)[] {
  const ppm = Math.max(1, pointsPerMember);
  return data.ignitions.map((ignition) => {
    const idx = ignitionPartitionIndex(ignition);
    if (idx === null) return null;
    if (memberIdx !== null && Math.floor(idx / ppm) !== memberIdx) return null;
    if (impactedByPoint === null) return true;
    return impactedByPoint.get(idx % ppm) ?? null;
  });
}

/** Frozen simLayers entries are directly usable as SimLayerData. */
export function simLayerFromFrozen(
  frozen: FrozenSimLayer | null | undefined
): SimLayerData | null {
  if (!frozen || !Array.isArray(frozen.ignitions) || frozen.ignitions.length === 0) {
    return null;
  }
  return frozen;
}
